import React from 'react';
import { VotingAnalytics as VotingAnalyticsType, VoteType, VOTE_TYPES } from '@/services/reactionsService';

interface VotingAnalyticsProps {
  analytics: VotingAnalyticsType | null;
  className?: string;
  showDetailed?: boolean;
}

export function VotingAnalytics({ 
  analytics, 
  className = "",
  showDetailed = false 
}: VotingAnalyticsProps) {
  if (!analytics || analytics.totalVotes === 0) {
    return null;
  }

  const getConsensusLabel = (score: number) => {
    if (score >= 0.8) return { label: 'Strong Consensus', color: 'text-green-600', bg: 'bg-green-50' };
    if (score >= 0.6) return { label: 'Good Consensus', color: 'text-green-500', bg: 'bg-green-50' };
    if (score >= 0.4) return { label: 'Moderate Consensus', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    if (score >= 0.2) return { label: 'Weak Consensus', color: 'text-orange-600', bg: 'bg-orange-50' };
    return { label: 'No Consensus', color: 'text-red-600', bg: 'bg-red-50' };
  };

  const getControversyLabel = (score: number) => {
    if (score >= 0.7) return { label: 'High Controversy', color: 'text-red-600' };
    if (score >= 0.5) return { label: 'Moderate Controversy', color: 'text-orange-600' };
    if (score >= 0.3) return { label: 'Some Controversy', color: 'text-yellow-600' };
    return { label: 'Low Controversy', color: 'text-green-600' };
  };

  const consensus = getConsensusLabel(analytics.consensusScore);
  const controversy = getControversyLabel(analytics.controversyScore);

  const voteBreakdown = [
    { type: 'upvotes', count: analytics.upvotes, data: VOTE_TYPES.upvote },
    { type: 'downvotes', count: analytics.downvotes, data: VOTE_TYPES.downvote },
    { type: 'mustHaveVotes', count: analytics.mustHaveVotes, data: VOTE_TYPES.must_have },
    { type: 'niceToHaveVotes', count: analytics.niceToHaveVotes, data: VOTE_TYPES.nice_to_have },
    { type: 'skipVotes', count: analytics.skipVotes, data: VOTE_TYPES.skip },
    { type: 'neutralVotes', count: analytics.neutralVotes, data: VOTE_TYPES.neutral },
  ].filter(item => item.count > 0);

  if (showDetailed) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 space-y-4 ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900">Voting Analytics</h4>
          <div className="text-xs text-gray-500">
            {analytics.totalVotes} vote{analytics.totalVotes !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Consensus Score */}
        <div className={`rounded-lg p-3 ${consensus.bg}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900">Group Consensus</span>
            <span className={`text-sm font-semibold ${consensus.color}`}>
              {Math.round(analytics.consensusScore * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-red-400 via-yellow-400 to-green-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${analytics.consensusScore * 100}%` }}
            />
          </div>
          <div className={`text-xs font-medium mt-1 ${consensus.color}`}>
            {consensus.label}
          </div>
        </div>

        {/* Vote Breakdown */}
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-3">Vote Distribution</h5>
          <div className="space-y-2">
            {voteBreakdown.map(({ type, count, data }) => {
              const percentage = (count / analytics.totalVotes) * 100;
              
              return (
                <div key={type} className="flex items-center gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-sm" style={{ color: data.color }}>
                      {data.icon}
                    </span>
                    <span className="text-sm text-gray-700 truncate">
                      {data.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${percentage}%`, 
                          backgroundColor: data.color 
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
          <div>
            <div className="text-xs text-gray-500">Avg. Strength</div>
            <div className="text-lg font-semibold text-gray-900">
              {analytics.averageStrength.toFixed(1)}x
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Engagement</div>
            <div className="text-lg font-semibold text-gray-900">
              {Math.round(analytics.engagementScore * 100)}%
            </div>
          </div>
        </div>

        {/* Controversy Alert */}
        {analytics.controversyScore > 0.5 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className={`text-sm font-medium ${controversy.color}`}>
                {controversy.label}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              This item has mixed reactions. Consider discussing with the group.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Compact view
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Quick Consensus Indicator */}
      <div className="flex items-center gap-2">
        <div className="w-12 bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-red-400 via-yellow-400 to-green-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${analytics.consensusScore * 100}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${consensus.color}`}>
          {Math.round(analytics.consensusScore * 100)}%
        </span>
      </div>

      {/* Top Vote Types */}
      <div className="flex items-center gap-1">
        {voteBreakdown.slice(0, 3).map(({ type, count, data }) => (
          <div
            key={type}
            className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-full text-xs"
            title={`${count} ${data.label} vote${count === 1 ? '' : 's'}`}
          >
            <span style={{ color: data.color }}>{data.icon}</span>
            <span className="text-gray-600 font-medium">{count}</span>
          </div>
        ))}
      </div>

      {/* Controversy Warning */}
      {analytics.controversyScore > 0.6 && (
        <div className="flex items-center gap-1">
          <svg className="w-3 h-3 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-xs text-orange-600 font-medium">Mixed</span>
        </div>
      )}
    </div>
  );
}

export function VotingStatsCard({ 
  analytics 
}: { 
  analytics: VotingAnalyticsType | null 
}) {
  if (!analytics) return null;

  const consensus = analytics.consensusScore >= 0.6 ? 'positive' : 
                   analytics.consensusScore >= 0.4 ? 'neutral' : 'negative';

  const consensusColor = {
    positive: 'text-green-600 bg-green-50 border-green-200',
    neutral: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    negative: 'text-red-600 bg-red-50 border-red-200'
  };

  return (
    <div className={`rounded-lg border p-3 ${consensusColor[consensus]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Group Opinion</span>
        <span className="text-lg font-bold">
          {Math.round(analytics.consensusScore * 100)}%
        </span>
      </div>
      <div className="text-xs opacity-75">
        {analytics.totalVotes} vote{analytics.totalVotes !== 1 ? 's' : ''} • 
        {analytics.controversyScore > 0.5 ? ' Mixed opinions' : ' Generally agreed'}
      </div>
    </div>
  );
}